import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Supplier } from '@/firebase/services/suppliers.service'
import { 
  getSuppliers as getSuppliersList,
  createSupplier as createSupplierService,
  updateSupplier as updateSupplierService,
  deleteSupplier as deleteSupplierService
} from '@/firebase/services/suppliers.service'
import { useCurrentOrganizationStore } from './currentOrganization'

export const useSuppliersStore = defineStore('suppliers', () => {
  const suppliers = ref<Supplier[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const currentOrganizationStore = useCurrentOrganizationStore()

  const activeSuppliers = computed(() => {
    return suppliers.value.filter(supplier => supplier.active)
  })

  const getSuppliers = async () => {
    if (!currentOrganizationStore.currentOrganization?.id) {
      console.error('No organization selected')
      return
    }

    loading.value = true
    error.value = null
    try {
      suppliers.value = await getSuppliersList(currentOrganizationStore.currentOrganization.id)
      return suppliers.value
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Erro ao carregar fornecedores'
      throw e
    } finally {
      loading.value = false
    }
  }

  const createSupplier = async (supplier: Omit<Supplier, 'id' | 'active' | 'createdAt' | 'updatedAt' | 'organizationId'>) => {
    if (!currentOrganizationStore.currentOrganization?.id) {
      throw new Error('No organization selected')
    }

    loading.value = true
    error.value = null
    try {
      const newSupplier = await createSupplierService(currentOrganizationStore.currentOrganization.id, supplier)
      if (newSupplier) {
        suppliers.value.push(newSupplier)
      }
      return newSupplier
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Erro ao criar fornecedor'
      throw e
    } finally {
      loading.value = false
    }
  }

  const updateSupplier = async (id: string, data: Partial<Supplier>) => {
    if (!currentOrganizationStore.currentOrganization?.id) {
      throw new Error('No organization selected')
    }

    loading.value = true
    error.value = null
    try {
      const updatedSupplier = await updateSupplierService(id, data)
      if (updatedSupplier) {
        const index = suppliers.value.findIndex(s => s.id === id)
        if (index !== -1) {
          suppliers.value[index] = { ...suppliers.value[index], ...updatedSupplier }
        }
      }
      return updatedSupplier
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Erro ao atualizar fornecedor'
      throw e
    } finally {
      loading.value = false
    }
  }

  const deleteSupplier = async (id: string) => {
    if (!currentOrganizationStore.currentOrganization?.id) {
      throw new Error('No organization selected')
    }

    loading.value = true
    error.value = null
    try {
      await deleteSupplierService(id)
      suppliers.value = suppliers.value.filter(s => s.id !== id)
      return true
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Erro ao excluir fornecedor'
      throw e
    } finally {
      loading.value = false
    }
  }

  return {
    suppliers,
    loading,
    error,
    activeSuppliers,
    getSuppliers,
    createSupplier,
    updateSupplier,
    deleteSupplier
  }
})
